import { useState, useEffect, useMemo } from 'react'
import filterOptionsData from './data/filter-options.json'
import imageDatabaseData from './data/image-database.json'

export function useDataExplorer(isOpen, onClose) {
  const [filterOptions] = useState(filterOptionsData)
  const [imageDatabase] = useState(imageDatabaseData)
  const [selectedFilters, setSelectedFilters] = useState({})
  const [filteredImages, setFilteredImages] = useState([])
  const [displayedImages, setDisplayedImages] = useState([])
  const [selectedItem, setSelectedItem] = useState(null)
  const [loading, setLoading] = useState(true)
  const [displayCount, setDisplayCount] = useState(50)
  const [hasMore, setHasMore] = useState(true)
  const [activeTab, setActiveTab] = useState('gallery')
  const [viewMode, setViewMode] = useState('masonry')
  
  // Map tab state
  const [currentSiteIndex, setCurrentSiteIndex] = useState(0)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  // Group images by site
  const siteGroups = useMemo(() => {
    const groups = {}
    imageDatabase.forEach(item => {
      if (!groups[item.site_name]) {
        groups[item.site_name] = []
      }
      groups[item.site_name].push(item)
    })
    return groups
  }, [imageDatabase])

  const siteNames = useMemo(() => Object.keys(siteGroups).sort(), [siteGroups])

  const currentSite = siteNames[currentSiteIndex]
  const currentSiteImages = siteGroups[currentSite] || []
  const currentImage = currentSiteImages[currentImageIndex]

  // Site navigation
  const navigateToNextSite = () => {
    setCurrentSiteIndex((prev) => (prev + 1) % siteNames.length)
    setCurrentImageIndex(0)
  }

  const navigateToPrevSite = () => {
    setCurrentSiteIndex((prev) => (prev - 1 + siteNames.length) % siteNames.length)
    setCurrentImageIndex(0)
  }

  const selectSite = (index) => {
    if (index >= 0 && index < siteNames.length) {
      setCurrentSiteIndex(index)
      setCurrentImageIndex(0)
    }
  }

  // Image navigation within site
  const navigateToNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % currentSiteImages.length)
  }

  const navigateToPrevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + currentSiteImages.length) % currentSiteImages.length)
  }

  // Initialize on mount
  useEffect(() => {
    setFilteredImages(imageDatabaseData)
    setDisplayedImages(imageDatabaseData.slice(0, 50))
    setLoading(false)
    
    // Initialize selected filters structure
    const initialFilters = {}
    Object.keys(filterOptionsData).forEach(key => {
      initialFilters[key] = []
    })
    setSelectedFilters(initialFilters)
  }, [])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Keyboard navigation - ESC to close (MediaModal handles arrow keys)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !selectedItem) {
        // Only close explorer if media modal is not open
        onClose()
      }
    }
    
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, selectedItem, onClose])

  // Filter images whenever selected filters change
  useEffect(() => {
    if (!imageDatabase.length) return

    const filtered = imageDatabase.filter(item => {
      // Check each filter category
      for (const [category, selectedOptions] of Object.entries(selectedFilters)) {
        if (selectedOptions.length === 0) continue

        // Handle different field types
        if (category === 'organisms' || category === 'cephalopod_behavior') {
          // Array fields - check if any selected option is in the item's array
          const hasMatch = selectedOptions.some(option => 
            item[category].includes(option)
          )
          if (!hasMatch) return false
        } else if (category === 'flash') {
          // Flash is boolean in data but string in filter options
          const itemFlashLabel = item.flash ? 'With flash' : 'Without flash'
          if (!selectedOptions.includes(itemFlashLabel)) return false
        } else {
          // Single value fields - check if item's value is in selected options
          if (!selectedOptions.includes(item[category])) return false
        }
      }
      return true
    })

    setFilteredImages(filtered)
    setDisplayedImages(filtered.slice(0, 50))
    setDisplayCount(50)
    setHasMore(filtered.length > 50)
  }, [selectedFilters, imageDatabase])

  // Load more images when scrolling
  const loadMoreImages = () => {
    const newCount = displayCount + 50
    setDisplayedImages(filteredImages.slice(0, newCount))
    setDisplayCount(newCount)
    setHasMore(newCount < filteredImages.length)
  }

  // Scroll handler for infinite scroll
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollHeight - scrollTop <= clientHeight * 1.2 && hasMore && !loading) {
      loadMoreImages()
    }
  }

  // Filter toggle handler
  const handleFilterToggle = (category, option) => {
    setSelectedFilters(prev => {
      const categoryFilters = prev[category] || []
      const isSelected = categoryFilters.includes(option)
      
      return {
        ...prev,
        [category]: isSelected
          ? categoryFilters.filter(item => item !== option)
          : [...categoryFilters, option]
      }
    })
  }

  // Clear all filters
  const clearAllFilters = () => {
    const clearedFilters = {}
    Object.keys(filterOptions).forEach(key => {
      clearedFilters[key] = []
    })
    setSelectedFilters(clearedFilters)
  }

  return {
    // Data state
    filterOptions,
    selectedFilters,
    displayedImages,
    filteredImages,
    loading,
    hasMore,
    selectedItem,
    activeTab,
    viewMode,
    
    // Map tab state
    currentSite,
    currentSiteImages,
    currentImage,
    currentSiteIndex,
    currentImageIndex,
    siteNames,
    
    // Actions
    setSelectedItem,
    handleFilterToggle,
    handleScroll,
    clearAllFilters,
    setActiveTab,
    setViewMode,
    navigateToNextSite,
    navigateToPrevSite,
    navigateToNextImage,
    navigateToPrevImage,
    selectSite,
  }
}


